/**
 * A collection of helper functions
 *
 * @packageDocumentation
 * @category Util
 */

import { COUNTRY_DETAILS } from 'BomdisoftUtilities/constants';
//#region imports
import type { SupabaseClient } from '@supabase/supabase-js';

//#endregion

type PageLink = {
  url: string;
  as: string;
};
export const getPathname = (link?: PageLink) => link?.as?.split('/').pop() || '/';

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 capitalizeFirstLetters
 *--------------------------------------------------------------------------------------------------------------------------
 *
 * Capitalize first letter of every word in a string
 * Only the first letter of every word is capitalized. The rest of the string is converted to lower case.
 * @param {String} text The string to be formatted
 * @returns {String} Formatted text with first letter of each word capitalized. Empty string if no name is provided
 */
export function capitalizeFirstLetters(name: string) {
  if (!name) return '';

  const words = name?.trim().split(' ');
  let formatted = '';

  for (let i = 0, len = words.length; i < len; i++) formatted += `${words[i].charAt(0).toUpperCase()}${words[i].substring(1).toLowerCase()} `;

  return formatted?.trim();
}

export type FormatMoneyOptions = {
  locale?: string;
  currencyCode?: string | null;
  currencyDisplay?: 'symbol' | 'narrowSymbol' | 'code' | 'name';
};

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 formatMoney
 *--------------------------------------------------------------------------------------------------------------------------
 *
 * Converts a number into a formatted currency string
 *
 * @example
 * const formatted = formatMoney(25841);
 * console.log(formatted);
 * // Console output: USD 25,841.00
 *
 * @param {number} amount The number to be formatted
 * @param {FormatMoneyOptions} [options] Format options
 * @returns {string} Formatted currency string
 */
export const formatMoney = (amount: number, options: FormatMoneyOptions = {}): string => {
  if (amount == null || Number.isNaN(amount)) amount = 0;
  if (!options.currencyDisplay) options.currencyDisplay = 'symbol';
  const { currencyCode, locale, currencyDisplay } = {
    currencyCode: 'XAF',
    locale: 'en-US',
    ...options,
  };

  return new Intl.NumberFormat(locale, {
    currency: currencyCode?.toUpperCase() || 'XAF',
    currencyDisplay,
    style: 'currency',
  }).format(amount);
};

type FormatNumberOptions = {
  locale?: string;
  prefix?: string;
  suffix?: string;
};

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 formatNumber
 *--------------------------------------------------------------------------------------------------------------------------

 * Converts a number into a formatted string
 *
 * @example
 * const formatted = formatNumber(25841);
 * console.log(formatted);
 * // Console output: 25,841
 *
 * @param {number} amount The number to be formatted
 * @param {FormatNumberOptions} [options] Format options
 * @returns {string} Formatted string
*/
export const formatNumber = (amount: number, options?: FormatNumberOptions): string => {
  if (amount == null || Number.isNaN(amount)) return '0';
  const { prefix, suffix, locale } = {
    locale: 'en-US',
    prefix: '',
    suffix: '',
    ...options,
  };

  const formatted = new Intl.NumberFormat(locale).format(amount);
  return `${prefix} ${formatted} ${suffix}`;
};

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 getRandomInt
 *--------------------------------------------------------------------------------------------------------------------------
 *
 * Generates and returns a random integer between `min`and `max`
 *
 * @example
 * const randNumber = getRandomInt(0, 10);
 * console.log(randNumber);
 * // Console output: 3
 *
 * @param {number} max The maximum number that can be generated
 * @param {number} min The minimum number that can be generated
 * @returns {number} The generated number returned as an integer
 */
export const getRandomInt = (max: number, min = 0) => {
  if (!max || typeof max !== 'number' || typeof min !== 'number') return 0;

  min = Math.ceil(min);
  max = Math.floor(max);

  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 parseJSON
 *--------------------------------------------------------------------------------------------------------------------------
 *
 * Parse a possible `null`, `undefined` or malformed JSON string without throwing errors
 *
 * @param {string} value JSON string to be parsed
 * @returns {(JSON|NULL)} A JSON object if parse is successful, NULL otherwise
 */
export function parseJSON(value?: string | null) {
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch (e) {
    if (process.env.NEXT_PUBLIC_IS_DEV) console.error(e);
    return null;
  }
}

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 openInNewTab
 *--------------------------------------------------------------------------------------------------------------------------
 *
 * Opens the provided url in a new browser tab
 *
 * @param {string} url URL to open
 */
export function openInNewTab(url: string) {
  const win = window.open(url, '_blank');
  win?.focus();
}

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 dummy
 *--------------------------------------------------------------------------------------------------------------------------
 *
 * A dummy function that does nothing.
 * Can be used as a default value for functions/components that accept an optional function parameter.
 */
export const dummy = () => {
  /**do nothing */
};

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 initializeOptions
 *--------------------------------------------------------------------------------------------------------------------------
 *
 */
export const initializeOptions = (value: string) => ({
  isValid: !(value?.trim().length < 1),
  showErrors: true,
  value,
});

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 initializeFileOptions
 *--------------------------------------------------------------------------------------------------------------------------
 *
 */
export function initializeFileOptions(value: string, files: File[] | null) {
  const options = { files, isValid: true, showErrors: true, value };
  if (!options.files || options.value?.trim().length < 1) options.isValid = false;

  return options;
}

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 isSuperuser
 *--------------------------------------------------------------------------------------------------------------------------
 *
 */
export const isSuperuser = (role?: string | null) => Boolean(role && role.toLowerCase() == 'superuser');

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 isAdminOrSuperuser
 *--------------------------------------------------------------------------------------------------------------------------
 *
 */
export const isAdminOrSuperuser = (role?: string | null) => Boolean(role && ['admin', 'superuser'].includes(role.toLowerCase()));

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 isAdmin
 *--------------------------------------------------------------------------------------------------------------------------
 *
 */
export const isAdmin = (role?: string | null) => Boolean(role && role.toLowerCase() == 'admin');

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 isUser
 *--------------------------------------------------------------------------------------------------------------------------
 *
 */
export const isUser = (role?: string | null) => Boolean(role && role.toLowerCase() === 'user');

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 isTutor
 *--------------------------------------------------------------------------------------------------------------------------
 *
 */
export const isTutor = (role?: string | null) => Boolean(role && role.toLowerCase() === 'tutor');

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 isStudent
 *--------------------------------------------------------------------------------------------------------------------------
 *
 */
export const isStudent = (role?: string | null) => Boolean(role && role.toLowerCase() === 'student');

export type CanModifyUserDataParams = {
  actor?: { id: string; role: string } | null; // User performing modification
  user: { id: string; role: string } | null; // User to be modified
};

export const canModifyUserData = ({ actor, user }: CanModifyUserDataParams): boolean => {
  if (!actor || !user) return false;
  if (actor.id === user.id) return true;
  if (actor.role === user.role) return false;

  return actor.id === user.id || (isSuperuser(actor.role) && !isSuperuser(user.role)) || (isAdmin(actor.role) && !isAdminOrSuperuser(user.role));
};

export type GetUniqueByIdParams = {
  id: number | string;
  [key: string]: any;
};
/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 getUniqueById
 *--------------------------------------------------------------------------------------------------------------------------
 * *Optimized for performance
 *
 * @param {T} arrayRangeParams
 * @returns {Array<T>} array of unique objects
 */
export const getUniqueById = <T extends { id?: string | number | null }>(data: T[]): T[] => {
  if (!data || data.length < 1) return [];

  const memo: { [key: string]: boolean } = {};
  const out: T[] = [];
  const len = data.length;

  let j = 0;
  for (let i = 0; i < len; i++) {
    if (!data[i].id) continue;
    const item = data[i].id!.toString();

    if (!memo[item]) {
      memo[item] = true;
      out[j++] = data[i];
    }
  }

  return out;
};

export type UploadResult = {
  success: 0 | 1;
  file?: { url: string };
};

export const uploadByFileToSupabase =
  (supabase: SupabaseClient) =>
  (file: File): Promise<UploadResult> =>
    new Promise((resolve, reject) => {
      const fileExt = file.name.split('.').pop();
      const fileName = `err_${new Date().getTime()}_.${fileExt}`;
      const filePath = `${fileName}`;

      supabase.storage
        .from('course_content')
        .upload(filePath, file)
        .then(() => {
          const {
            data: { publicUrl },
          } = supabase.storage.from('course_content').getPublicUrl(filePath);

          if (!publicUrl) return reject({ success: 0 });
          return resolve({ file: { url: publicUrl }, success: 1 });
        });
    });

export const uploadByFileToFirebase = (file: File) =>
  new Promise((resolve, reject) => {
    if (!file) return reject({ success: 0 });
    return resolve({ file: { url: '' }, success: 1 }); // TODO: save file to backend and replace with actual url
  });

export const getCountryFlag = (code: string) => COUNTRY_DETAILS.find(country => country.code === code)?.flag;

type SortableObject = {
  hierarchy: number;
  [key: string]: any;
};
export const sortByHierarchy = (obj1: SortableObject, obj2: SortableObject) => {
  if (!obj1.hierarchy || !obj2.hierarchy) return 0;
  if (Number(obj1.hierarchy) < Number(obj2.hierarchy)) return -1;
  if (Number(obj1.hierarchy) > Number(obj2.hierarchy)) return 1;

  return 0;
};

export const sortEntriesByHierarchy = (entry1: SortableObject[], entry2: SortableObject[]) => {
  if (!entry1[1].hierarchy || !entry2[1].hierarchy) return 0;
  if (Number(entry1[1].hierarchy) < Number(entry2[1].hierarchy)) return -1;
  if (Number(entry1[1].hierarchy) > Number(entry2[1].hierarchy)) return 1;

  return 0;
};

type SortableInput = {
  hierarchy: {
    value: string;
  };
  [key: string]: any;
};
export const sortInputByHierarchy = (obj1: SortableInput, obj2: SortableInput) => {
  if (!obj1?.hierarchy.value || !obj2?.hierarchy.value) return 0;
  if (Number(obj1.hierarchy.value) < Number(obj2.hierarchy.value)) return -1;
  if (Number(obj1.hierarchy.value) > Number(obj2.hierarchy.value)) return 1;

  return 0;
};

export function initializeImageOptions(value: string, image: any) {
  const options = {
    image,
    isValid: !(value?.trim().length < 1),
    showErrors: true,
    value,
  };

  return options;
}

export function initializeVideoOptions(video: any, url: string) {
  const options = {
    duration: video.duration,
    hierarchy: initializeOptions(''),
    id: initializeOptions(''),
    isValid: true,
    name: video.name,
    showErrors: true,
    size: video.size,
    title: initializeOptions(video.name.substr(0, video.name.lastIndexOf('.'))),
    type: initializeOptions(video.type),
    url,
    video,
  };
  if (options.url?.trim().length < 1) options.isValid = false;

  return options;
}

export function removeToken() {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem('token');
  } catch (e) {
    console.log(e);
  }
}

export function videoIsInFullscreen() {
  if (document.fullscreenElement && document.fullscreenElement.nodeName == 'VIDEO') {
    return true;
  }
  return false;
}

export function storageAvailable(type: string) {
  let storage, x;

  try {
    storage = (window as { [key: string]: any })[type];
    x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return e instanceof DOMException && (e.code === 22 || e.code === 1014 || e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED') && storage.length !== 0;
  }
}

type GetDeviceLocationParams = {
  callback: (position: GeolocationPosition) => void;
  errorHandler?: () => void;
};
export type GeocodeData = {
  location: { latitude: number; longitude: number; name: string };
  address: string;
  coordinates: string;
  city?: string;
  county?: string;
  country?: string;
  country_code?: string;
  postcode?: string;
};
export const getDeviceLocation = ({ callback, errorHandler }: GetDeviceLocationParams) => {
  console.log(`Attempting user's current location...`);

  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(callback, () => {
      console.error(`Geolocation was not successful`);
      if (typeof errorHandler === 'function') errorHandler();
    });
  } else {
    console.error(`Browser doesn't support geolocation`);
    if (typeof errorHandler === 'function') errorHandler();
  }
};

const getFormattedGeocodeResponse = (selected: any): GeocodeData => {
  const latitude = selected.geometry.location.lat();
  const longitude = selected.geometry.location.lng();

  const data: GeocodeData = {
    address: selected.formatted_address,
    coordinates: `${latitude},${longitude}`,
    location: { latitude, longitude, name: selected.formatted_address },
  };

  for (let i = 0; i < selected.address_components.length; i++) {
    if (selected.address_components[i].types[0] == 'locality') data.city = selected.address_components[i].long_name;
    if (selected.address_components[i].types[0] == 'administrative_area_level_1') data.county = selected.address_components[i].long_name;
    if (selected.address_components[i].types[0] == 'country') {
      data.country = selected.address_components[i].long_name;
      data.country_code = selected.address_components[i].short_name;
    }
    if (selected.address_components[i].types[0] == 'postal_code') data.postcode = selected.address_components[i].long_name;
  }

  return data;
};

export const getGeocodeDataFromCoordinates = (location: { lat: number; lng: number }): Promise<GeocodeData | null> =>
  new Promise((resolve, reject) => {
    new window.google.maps.Geocoder().geocode({ location }, (response: any) => {
      if (Array.isArray(response) && response.length > 0) {
        const selected = response.find((r: any) => r.types.includes['street_address']) || response[0];
        const data = getFormattedGeocodeResponse(selected);

        resolve(data);
      } else {
        console.error(`Coordinates geocode was not successful: ${JSON.stringify(response)}`);
        reject(`Coordinates geocode was not successful: ${JSON.stringify(response)}`);
      }
    });
  });
export const getGeocodeDataFromAddress = (address: string): Promise<GeocodeData | null> =>
  new Promise((resolve, reject) => {
    new window.google.maps.Geocoder().geocode({ address }, (results: any[], status: string) => {
      if (status == 'OK') {
        const data = getFormattedGeocodeResponse(results[0]);
        resolve(data);
      } else {
        console.error(`Address geocode was not successful: ${status}`);
        reject(`Address geocode was not successful: ${status}`);
      }
    });
  });

export const getGeocodeDataFromDevice = (): Promise<GeocodeData | null> =>
  new Promise((resolve, reject) => {
    getDeviceLocation({
      callback: (position: GeolocationPosition) => {
        new window.google.maps.Geocoder().geocode(
          {
            location: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            },
          },
          (response: any) => {
            if (Array.isArray(response) && response.length > 0) {
              const selected = response.find((r: any) => r.types.includes['street_address']) || response[0];
              const data = getFormattedGeocodeResponse(selected);

              resolve(data);
            } else {
              console.error(`Coordinates geocode was not successful: ${JSON.stringify(response)}`);
              reject(`Coordinates geocode was not successful: ${JSON.stringify(response)}`);
            }
          },
        );
      },
      errorHandler: () => reject(`Coordinates geocode was not successful`),
    });
  });
